import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import SimpleLinearRegression from 'ml-regression-simple-linear';


import { chartColors } from '../../helpers/chartColors';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { getMetricName, getMetricParamName, getMetricUnits, getMetricValue } from './RTFOMetricsPanel';
import { useTheme } from '@mui/styles';
import { getSeismicEventColor } from './RTFOMapWithWellbore';
import { determineIsEventFilteredOut } from './RTFOSeismicChart';
import { Typography } from '@mui/material';
import { getSeismicName, getSeismicParamUnits, getSeismicParamValue } from './RTFOSeismicityPanel';

export const RTFOMetricsChart = ({ well, metric, type, showTrendline, showHistoric, averageMetrics, isDailyMetric }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const seismicData = useMemo(() => (currentRTFO?.seismicData?.['realtime'] || []), [currentRTFO?.seismicData]);

  const [seismicFilter, setSeismicFilter] = useState([]);

  const allWells = Object.keys(currentRTFO?.perfSchedule?.wells)?.sort((a, b) => a - b);
  const maxStageNumber = Math.max(...allWells?.map(wellName => parseInt(Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0])));

  const isMetricCumulative = 
    metric === 'totalSlurryVolume' || 
    metric === 'totalCleanVolume' || 
    metric === 'totalProducedVolume' || 
    metric === 'totalProppant' || 
    metric === 'totalFr' || 
    metric === 'totalFr2' ||
    metric === 'perfClosure' ||
    metric === 'plugSlip' ||
    metric === 'sandoff' ||
    metric === 'isolationIssue' ||
    metric === 'equipmentIssue' ||
    metric === 'lostSuction' ||
    metric === 'other' ||
    metric === 'fracfeed'
  ;

  const data = useMemo(() => {
    if (!currentRTFO?.stageData || !metric) return [];
    
    return !isDailyMetric ? getChartStageData() : getChartDailyData();

    function getChartStageData() {
      try {

        const wells = well === 'All' ? [...new Set(currentRTFO?.stages?.map(stageName => stageName.split('_')?.[1]))]?.sort() : [well];

        const traces = [];

        const X = Array.from(Array(maxStageNumber), (_, index) => parseInt(index) + 1);

        // add metrics trace
        for (const well of wells) {
          const wellIndex = wells?.indexOf(well);

          const allStages = Object.keys(currentRTFO?.stageData);

          const wellStages = allStages?.filter(stageName => {
            const stage = currentRTFO?.stageData?.[stageName];
            return stage?.wellName === well;
          }) || [];

          const Y = [];
          const XSeismicity = [];
          const YSeismicity = [];

          for (let i = 0; i < X?.length; i++) { // Stage numbers

            const stageNames = wellStages?.filter(s => (Number(String(s.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim())) === parseInt(X[i]));
            
            let stageName = stageNames?.[0] || '';
            if (stageNames?.length > 1) { // if repeat stages found
              const maxTotalProppant = Math.max(...stageNames?.map(name => currentRTFO?.stageData?.[name]?.metrics?.tpmCalc * currentRTFO?.stageData?.[name]?.metrics?.stageLength));
              stageName = stageNames?.find(s => (currentRTFO?.stageData?.[s]?.metrics?.tpmCalc *  currentRTFO?.stageData?.[s]?.metrics?.stageLength)?.toFixed(0) === maxTotalProppant?.toFixed(0));
            };

            const stage = currentRTFO?.stageData?.[stageName];

            let yValue = getMetricValue(metric, stage, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO?.monitorEvents, currentRTFO?.fiberData);

            Y.push(Number(yValue) || 0);
            
            // Add induced seismicity data
            if (seismicData?.length > 0) {
              const stageSeismicity = seismicData?.filter(e => (e?.stageName === stageName) || (e?.time >= stage?.stageStartTimestamp && e?.time <= (stage?.stageEndTimestamp || (stage?.stageStartTimestamp + stage?.metrics?.stageTime * 60000)) ) );
              for (const event of stageSeismicity) {
                const isEventFilteredOut = determineIsEventFilteredOut(seismicFilter, event?.localMagnitude);
  
                if (isEventFilteredOut) continue;
  
                XSeismicity.push(X[i]);
                YSeismicity.push(event.magnitude);
              };
            };

          };

          // add well traces
          const wellTrace = {
            x: X,
            y: Y,
            type: type === 'Bars' ? 'bar' : 'scattergl',
            mode: type === 'Lines' ? 'lines' : type === 'Scatter' && 'markers',
            xaxis: 'x1',
            yaxis: 'y1',
            name: well,
            marker: {
              color: chartColors[wellIndex],
              symbol: wellIndex === 0 ? 'circle' : wellIndex === 1 ? 'square' : wellIndex === 2 ? 'diamond' :  wellIndex === 3 ? 'triangle-down' : 'triangle-up',
              size: 10
            },
          };

          traces.push(wellTrace);


          // add seismicity traces
          if (seismicData?.length > 0) {
            const seismicityTrace = {
              x: XSeismicity,
              y: YSeismicity,
              type: 'scattergl',
              mode: 'markers',
              xaxis: 'x1',
              yaxis: 'y3',
              name: `${well} Seismicity`,
              marker: {
                color: YSeismicity.map(m => getSeismicEventColor(m)),
                symbol: 'circle',
                size: 10
              },
            };
  
            traces.push(seismicityTrace);
          };

          // add trendlines
          if (showTrendline) {
            // calculate trendline
            const XTrendline = [];
            let YTrendline = [];
            Y.forEach((val,i) => {
              if (!isNaN(val) && isFinite(val) && val > 0 && !isNaN(X[i]) && isFinite(X[i])) {
                YTrendline.push(val);
                XTrendline.push(X[i]);
              };
            });

            const regression = new SimpleLinearRegression(XTrendline, YTrendline);
  
            YTrendline = XTrendline?.map(x => regression.predict(x));

            const trendlineTrace = {
              x: XTrendline,
              y: YTrendline,
              type: 'scattergl',
              mode: 'lines',
              xaxis: 'x1',
              yaxis: 'y1',
              name: `${well} Trendline`,
              line: {
                color: chartColors[wellIndex],
                width: 3,
                dash: 'dash'
              },
            };
            traces.push(trendlineTrace);
          };
        };

        // add cumulative metrics trace
        if (isMetricCumulative) {
          const YPad = [];
          const YPadCum = [];

          for (const well of wells) {
            const wellIndex = wells.indexOf(well);
            const YCum = [];
            for (let stageNumber = 1; stageNumber <= maxStageNumber; stageNumber++) {
              const index = stageNumber - 1;
              const stageName = `${currentRTFO?.name}_${well?.charAt(0)?.toUpperCase()?.concat(well?.substring(1, ))}_${stageNumber}`;
              const stage = currentRTFO?.stageData?.[stageName];
              const value = getMetricValue(getMetricParamName(metric), stage, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO?.monitorEvents);
              // const value = stage?.metrics?.[metric] || 0;
              const cumValue = value + (stageNumber === 1 ?  0  : YCum[index - 1]);
              YCum.push(cumValue);
              YPad[index] = [...(YPad[index] || []), value];
            };
            const wellCumTrace = {
              x: X,
              y: YCum,
              type: 'scattergl',
              mode: 'lines',
              xaxis: 'x1',
              yaxis: 'y2',
              name: `${well} Cumulative`,
              line: {
                color: chartColors[wellIndex],
                width: 3,
              },
            };
            traces.push(wellCumTrace);
          };

          YPad.forEach((d,i) => {
            const value = d.reduce((tot, curr) => tot + curr, 0) || 0;
            YPadCum.push(value + (i === 0 ? 0 : YPadCum[i-1]));
          })

          const padCumTrace = {
            x: X,
            y: YPadCum,
            type: 'scattergl',
            mode: 'lines',
            xaxis: 'x1',
            yaxis: 'y2',
            name: `Pad Cumulative`,
            line: {
              color: 'black',
              width: 3,
            },
          };
          traces.push(padCumTrace);
        };


        // add average metrics trace
        if (showHistoric && averageMetrics) {
          const previousPad = (averageMetrics && Object.keys(averageMetrics)?.[0]) || {};
          const avgX = Object.keys(averageMetrics?.[previousPad])?.filter(d => parseInt(d) <= maxStageNumber);
          const avgY = [];

          avgX?.forEach(stageNumber => {
            if (!isNaN(Number(averageMetrics?.[previousPad]?.[stageNumber]?.[metric]))) {
              avgY.push(Number(averageMetrics?.[previousPad]?.[stageNumber]?.[metric]));
            };
          });

          if (avgY?.length > 0) {
            const averageMetricsTrace = {
              x: avgX,
              y: avgY,
              type: 'scattergl',
              mode: 'lines',
              xaxis: 'x1',
              yaxis: 'y1',
              name: `${previousPad} Average`,
              line: {
                color: darkMode ? 'lightgrey' : '#333',
                width: 3,
                dash: 'dash'
              }
            };
            traces.push(averageMetricsTrace);
          };
        };

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };

    function getChartDailyData() {
      try {
        const startTimestamp = currentRTFO?.stageData[currentRTFO?.stages?.[0]]?.stageStartTimestamp;
        let endTimestamp = new Date(currentRTFO?.stageData[currentRTFO?.stages?.[currentRTFO?.stages?.length - 1]]?.stageEndTimestamp);
        // Set the time to the end of the day
        endTimestamp.setHours(23);
        endTimestamp.setMinutes(59);
        endTimestamp.setSeconds(59);
        endTimestamp.setMilliseconds(999);
        endTimestamp = endTimestamp.getTime();
        const calendarDates = getUniqueDates(startTimestamp, endTimestamp);

        const traces = [];

        const X = [];
        const Y = [];
        const XSeismicity = [];
        const YSeismicity = [];
        
        for (const date of calendarDates) {
          const dateValue = new Date(date);
          X.push(dateValue);
          const yValue = getSeismicParamValue(metric, currentRTFO, date);
          Y.push(yValue);


          // add seismicity data
          for (const event of seismicData) {
            const isEventFilteredOut = determineIsEventFilteredOut(seismicFilter, event?.localMagnitude);

            if (new Date(event.time)?.toLocaleDateString() === date && !isEventFilteredOut) {
              XSeismicity.push(dateValue);
              YSeismicity.push(event.localMagnitude || null);
            };
          };
        };

        // add well traces
        const wellTrace = {
          x: X,
          y: Y,
          type: type === 'Bars' ? 'bar' : 'scattergl',
          mode: type === 'Lines' ? 'lines' : type === 'Scatter' && 'markers',
          xaxis: 'x1',
          yaxis: 'y1',
          name: !isDailyMetric ? getMetricName(metric) : getSeismicName(metric),
          marker: {
            color: chartColors[0],
            symbol: 'circle',
            size: 10
          },
        };

        traces.push(wellTrace);

        // add seismicity traces
        if (XSeismicity?.length > 0) {

          const seismicityTrace = {
            x: XSeismicity,
            y: YSeismicity,
            type: 'scattergl',
            mode: 'markers',
            xaxis: 'x1',
            yaxis: 'y3',
            name: `Seismicity`,
            marker: {
              color: YSeismicity.map(m => getSeismicEventColor(m)),
              symbol: 'circle',
              size: 10
            },
          }

          traces.push(seismicityTrace);
        }

        


        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };

      function getUniqueDates(startTimestamp, endTimestamp) {
        const dates = [];
        const currentDate = new Date(startTimestamp);
        let attempt = 0;

        while (currentDate.getTime() <= endTimestamp && attempt <= 999) {
          const formattedDate = currentDate.toLocaleDateString();
          dates.push(formattedDate);
          currentDate.setDate(currentDate.getDate() + 1);
          attempt++;
        }
      
        return dates;
      }
    };
  }, [currentRTFO, metric, isDailyMetric, well, maxStageNumber, isMetricCumulative, showHistoric, averageMetrics, type, seismicData, showTrendline, seismicFilter, darkMode]);

  const layout = {
    // barmode: 'group',
    yaxis: { // Main y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `${!isDailyMetric ? getMetricName(metric) : getSeismicName(metric)} ${(!isDailyMetric ? getMetricUnits(metric) : getSeismicParamUnits(metric))?.length > 0 ? `(${!isDailyMetric ? getMetricUnits(metric) : getSeismicParamUnits(metric)})` : ""}`,
        font: {
          size: 12
        },
      },
      anchor: isMetricCumulative ? 'free' : 'x',
      autorange: true,
      side: 'left',
      uirevision: 'true',
      rangemode: 'nonnegative'
    },
    yaxis2: { // Cum y axis
      linecolor: 'black',
      showgrid: false,
      color: 'black',
      title: {
        text: `Cumulative ${getMetricName(metric)} ${getMetricUnits(metric)?.length > 0 ? `(${getMetricUnits(metric)})` : ''}`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      side: 'left',
      overlaying: 'y',
      uirevision: 'true',
      type: 'log'
    },
    yaxis3: { // Seismicity y axis
      linecolor: 'black',
      showgrid: false,
      color: 'black',
      title: {
        text: `Local Magnitude (ML)`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [-1, 5],
      side: 'right',
      overlaying: 'y',
      uirevision: 'true',
      zeroline: false
    },
    xaxis: { // Main x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: !isDailyMetric ? 'Stage' : 'Date',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      domain: isMetricCumulative ? [0.05, 1] : [0, 1],
      tickformat: !isDailyMetric && '.0f',
      tickvals: !isDailyMetric && Array.from(Array(maxStageNumber), (_, index) => index + 1),
      type: isDailyMetric && 'date'
    },
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.15
    },
    annotations: [
      {
        text: !isDailyMetric ? getMetricName(metric) : getSeismicName(metric),
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.125, //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    uirevision: 'true',
    margin: {"t": 40, "l": 60, "r": 50, "b": 0},
    images: [
      {
        x: 0.025,
        y: 1.015,
        sizex: 0.05,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  function handleLegendItemClick(seismicValue) {
    return seismicFilter.includes(seismicValue) ? setSeismicFilter(prev => prev.filter(d => d !== seismicValue)) : setSeismicFilter(prev => [...prev, seismicValue]);
  };

  return (
    <div style={{position: 'relative', width: '100%', height: '100%'}}>

      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
      />

      {seismicData?.length > 0 && 
      <div style={{position: 'absolute', width: 800, left: '50%', transform: 'translateX(-400px)', top: 20}}>
        <div style={{display: 'flex', flexDirection: 'row'}}>
          {[0.75, 1.5, 2.5, 3.5, 4.5].map(seismicValue => {
            return (
            <span 
              style={{cursor: 'pointer', opacity: seismicFilter.includes(seismicValue) ? 0.25 : 1, display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 20}}
              onClick={() => handleLegendItemClick(seismicValue)}
              key={seismicValue}
            >
              <div style={{height: 10, width: 10, borderRadius: '50%', backgroundColor: getSeismicEventColor(seismicValue), marginRight: 5}}></div>
              <Typography fontSize='12px'>{
                seismicValue < 1 ? 'Local Magnitude < 1' :
                seismicValue < 2 ? 'Local Magnitude 1 - 2' :
                seismicValue < 3 ? 'Local Magnitude 2 - 3' :
                seismicValue < 4 ? 'Local Magnitude 3 - 4' :
                seismicValue < 5 ? 'Local Magnitude > 4' : ''
              }</Typography>
            </span>
            )
          })}
          
        </div>
      </div>}
    </div>
  );

  
};

// function getMetricValue(currentRTFO, stage, metric) {
//   let yValue = 0;
//   if (metric === 'aSqrtK') {
//     yValue = stage?.fracModel?.aSqrtK;
//   } else if (metric === 'fracArea') {
//     yValue = stage?.fracModel?.fracArea;
//   } else if (metric === 'halfLength') {
//     yValue = (stage?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight * 2)) || 0;
//   } else if (metric === 'perfClosure') {
//     yValue = (stage?.events?.perfClosure)?.length || 0;
//   } else if (metric === 'plugSlip') {
//     yValue = (stage?.events?.plugSlip)?.length || 0;
//   } else if (metric === 'sandoff') {
//     yValue = stage?.events?.sandoff?.length || 0;
//   } else if (metric === 'isolationIssue') {
//     yValue = stage?.events?.isolationIssue?.length || 0;
//   } else if (metric === 'equipmentIssue') {
//     yValue = stage?.events?.equipmentIssue?.length || 0;
//   } else if (metric === 'lostSuction') {
//     yValue = stage?.events?.lostSuction?.length || 0;
//   } else if (metric === 'other') {
//     yValue = stage?.events?.other?.length || 0;
//   } else if (metric === 'fracfeed') {
//     yValue = stage?.events?.fracfeed?.length || 0;
//   } else if (metric === 'totalProppant') {
//     yValue = stage?.metrics?.tpmCalc * stage?.metrics?.stageLength;
//   } else if (metric === 'tpm') {
//     yValue = currentRTFO?.perfSchedule?.wells?.[stage?.wellName?.toLowerCase()]?.stages?.[String(Number(String(stage?.name.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()))]?.tpm || 0;
//   } else if (metric === 'dfr') {
//     yValue = (stage?.metrics?.['frPerFluid'] * stage?.metrics?.['totalCleanVolume']) || 0
//   } else if (metric === 'lfr') {
//     yValue = (stage?.metrics?.['frPerFluid2'] * stage?.metrics?.['totalCleanVolume']) || 0
//   } else {
//     yValue = stage?.metrics?.[metric];
//   };
//   return yValue || 0;
// };

export const RTFOMetricsChartMemo = React.memo(RTFOMetricsChart);